/**
 * Floating monitor-picker chip — rendered over the session video when the host advertises more
 * than one monitor. Single-tap opens a dropdown listing every monitor (label + resolution);
 * picking one fires `monitor_select` via the parent hook.
 *
 * Visual language: matches the brushed-gold theme — semi-transparent obsidian panel with a hair
 * gold ring; the dot next to each entry uses the same emerald colour as the "online" indicator
 * on `MachinesPage` to stay consistent with the rest of the dashboard.
 */

import { useEffect, useRef, useState } from 'react';
import type { MonitorInfo } from '../clipboard/protocol';

interface Props {
  monitors: MonitorInfo[];
  onSelect: (index: number) => void;
}

export function MonitorChip({ monitors, onSelect }: Props) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  // Close the dropdown when the user clicks anywhere else. We attach the listener globally —
  // the chip is small + the dropdown is the only interactive thing on top of the video, so
  // a blanket `mousedown` listener is the simplest correct option.
  useEffect(() => {
    if (!open) return;
    const onDocDown = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node | null;
      if (target && wrapRef.current && !wrapRef.current.contains(target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocDown);
    document.addEventListener('touchstart', onDocDown, { passive: true });
    return () => {
      document.removeEventListener('mousedown', onDocDown);
      document.removeEventListener('touchstart', onDocDown);
    };
  }, [open]);

  // Single-monitor hosts don't need a picker — render nothing and stay out of the user's way.
  if (monitors.length <= 1) return null;

  const active = monitors.find((m) => m.active) ?? monitors[0];

  return (
    <div ref={wrapRef} className="absolute top-3 right-3 z-30">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium
                   bg-black/45 backdrop-blur border border-[var(--color-border)]
                   text-[var(--color-text)] hover:border-[var(--color-accent)] transition"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
        <span className="uppercase tracking-wider text-[10px] text-[var(--color-text-muted)]">
          Monitor
        </span>
        <span className="font-mono">{active.index + 1}</span>
        <span className="text-[var(--color-text-muted)] ml-0.5">▾</span>
      </button>

      {open && (
        <ul
          role="listbox"
          className="absolute right-0 mt-2 min-w-[14rem] py-1 rounded-md
                     bg-black/85 backdrop-blur-md border border-[var(--color-border)]
                     shadow-[0_20px_60px_-20px_rgba(0,0,0,0.7)]"
        >
          {monitors.map((m) => {
            const isActive = m.active;
            return (
              <li key={m.index}>
                <button
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  onClick={() => {
                    onSelect(m.index);
                    setOpen(false);
                  }}
                  className={`w-full flex items-center gap-2 text-left px-3 py-2 text-xs transition ${
                    isActive
                      ? 'text-[var(--color-accent)] bg-[var(--color-accent-soft)]'
                      : 'text-[var(--color-text)] hover:bg-white/5'
                  }`}
                >
                  <span
                    className={`w-1.5 h-1.5 rounded-full flex-none ${
                      isActive ? 'bg-emerald-400' : 'bg-[var(--color-border)]'
                    }`}
                  />
                  <span className="flex-1 truncate">
                    {m.name || `Monitor ${m.index + 1}`}
                  </span>
                  <span className="font-mono text-[10px] text-[var(--color-text-muted)]">
                    {m.width}×{m.height}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
